// People & Badges screens. Pure: data in, HTML out.
// Words: PEOPLE_BADGES_WORDS.md (approved 2026-10-02). Mockup: MOCKUP_PEOPLE_BADGES_APPROVED.html.
// LOTO verify: MOCKUP_QEW_DASHBOARD_APPROVED.html screen 6.
// Wide screens use the account-page layout; phones get the same content stacked (styles.css).
// Every change goes through server code (functions/src/people.ts) and logs who and when.

import type { Authority } from "./badge";
import { DEPARTMENTS, EXPERIENCE, ROLES } from "./claim";
import type { PeopleData } from "./firebase";
import { lotoCardOf, usDate } from "./loto";
import {
  LEVELS,
  OUT_OF_SERVICE_REASONS,
  ADMIN_AUTHORITIES,
  PERSON_AUTHORITIES,
  authorityLabel,
  authorityChangeLine,
  isAdminId,
  levelDefaults,
  levelLabel,
  lotoPending,
  lotoSourceLine,
  nextTagForSite,
  personState,
  tagCounts,
  tagsIn,
  type BadgeDoc,
  type NewPersonInput,
  type PeopleGroup,
  type Person,
  type PersonInput,
  type PersonRow,
  type PersonState,
  type TagBucket,
} from "./people";
import { esc, ident } from "./training-screens";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

const NOT_SET = '<span class="muted">Not set</span>';
function txt(v: unknown): string {
  return v === undefined || v === null || v === "" ? NOT_SET : esc(v);
}

function siteHeading(siteNumber: string, data: PeopleData): string {
  return siteNumber === "ALL" ? "All Sites" : `${siteNumber} ${data.siteNames[siteNumber] ?? `Site ${siteNumber}`}`;
}

const STATE_PILL: Record<PersonState, string> = {
  qualified: '<span class="pill green">ACTIVE</span>',
  training: '<span class="pill amber">IN TRAINING</span>',
  off: '<span class="pill grey">OFF</span>',
};

// ---- 1. People list ----

/**
 * site: People & Badges opened on one site from its hub (Home rev 3, 2026-10-23). Only that site's
 * people show, the back link returns to the hub, and Show All Sites opens the full list.
 */
export function peopleShell(data: PeopleData, pendingCount: number, onlyLoto: boolean, search: string, flash = "", site = ""): string {
  if (site) return sitePeopleShell(data, site, search, flash);
  const total = data.people.filter((p) => !isAdminId(p.qewId)).length;
  const active = data.badges.filter((b) => b.Status === "Active" && !isAdminId(b["Badge ID"])).length;
  const lotoNote = onlyLoto
    ? `<div class="note">Showing the ${pendingCount} LOTO ${pendingCount === 1 ? "date" : "dates"} waiting on verify. <a class="lnk" href="#/people">Show Everyone</a></div>`
    : pendingCount
      ? `<div class="note">${pendingCount} LOTO ${pendingCount === 1 ? "date is" : "dates are"} waiting on verify. QEW status waits on them. <a class="lnk" href="#/people/loto">Show Only These</a></div>`
      : "";
  return `${ident("PB", "People & Badges", `${total} people · ${active} working tags`)}
    ${flash}
    <div class="pb-actions"><a class="chg" href="#/people/add">Add Person</a><a class="chg" href="#/people/tags">Tags</a></div>
    ${lotoNote}
    <label class="search"><span class="sr">Search</span><input id="people-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search name or ID" autocomplete="off" autocapitalize="off" spellcheck="false"></label>
    <div id="people-groups"></div>`;
}

function sitePeopleShell(data: PeopleData, site: string, search: string, flash: string): string {
  const name = data.siteNames[site] ?? `Site ${site}`;
  const total = data.people.filter((p) => !isAdminId(p.qewId) && p.siteNumber === site).length;
  const active = data.badges.filter((b) => b.Status === "Active" && b["Badge ID"].startsWith(`${site}-`)).length;
  return `<a class="back" href="#/sites/${esc(site)}">${esc(name)}</a>
    ${ident("PB", "People & Badges", `${site} ${name} · ${total} people · ${active} working tags`)}
    ${flash}
    <div class="pb-actions"><a class="chg" href="#/people/add">Add Person</a><a class="chg" href="#/people/tags">Tags</a><a class="chg" href="#/people">Show All Sites</a></div>
    <label class="search"><span class="sr">Search</span><input id="people-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search name or ID" autocomplete="off" autocapitalize="off" spellcheck="false"></label>
    <div id="people-groups"></div>`;
}

function personRowHtml(r: PersonRow): string {
  const tag = r.isAdmin ? "Admin badge" : r.tagOut ? "Tag out of service" : r.state === "off" ? "Switched off" : "Tag claimed";
  const loto = r.lotoPending ? ' · <span class="warn-text">LOTO to verify</span>' : "";
  return `<a class="ar${r.state === "off" ? " dim" : ""}" href="#/people/p/${encodeURIComponent(r.qewId)}">
    <span class="chip${r.isAdmin ? " dark" : ""}">${esc(r.qewId)}</span>
    <span class="t"><b>${esc(r.name)}</b><small>${esc(r.level)} · ${tag}${loto}</small></span>
    ${STATE_PILL[r.state]}</a>`;
}

export function peopleGroups(groups: PeopleGroup[], data: PeopleData, search: string): string {
  if (!groups.length) {
    return `<div class="box"><div class="empty">${search.trim() ? `Nobody matches "${esc(search.trim())}".` : "Nobody here yet."}</div></div>`;
  }
  return groups
    .map(
      (g) => `<div class="grp"><span>${esc(siteHeading(g.siteNumber, data))}</span><span>${g.rows.length}</span></div>
      <div class="box">${g.rows.map(personRowHtml).join("")}</div>`,
    )
    .join("");
}

// ---- 2. One person ----

export function personDetail(p: Person, badge: BadgeDoc | undefined, data: PeopleData, nodesDone: boolean, today: string, flash = ""): string {
  const admin = isAdminId(p.qewId);
  const state = admin ? "qualified" : personState(p, badge, nodesDone);
  const site = admin ? "All Sites" : siteHeading(p.siteNumber, data);
  const id = encodeURIComponent(p.qewId);
  const auths = (badge?.Authorities ?? []) as Authority[];
  const level = levelLabel(p.level);
  const out = Boolean(badge?.outOfServiceOn);
  const badgeOn = badge?.Status === "Active";

  const activeEnd = admin
    ? '<span class="lock">Locked</span>'
    : out
      ? '<span class="lock">Tag out</span>'
      : `<button class="chg" type="button" data-act="person-active">${badgeOn ? "Turn Off" : "Turn On"}</button>`;
  const activeLine = admin
    ? "Admin badge"
    : out
      ? "Tag out of service. How a replacement is issued is not decided yet."
      : badgeOn
        ? state === "qualified"
          ? "Tag works. Qualified."
          : "Tag works. Still in training: Nodes 1 to 4 and a verified LOTO date."
        : "Switched off. Tag does not work. Name stays on past records.";

  const defaults = levelDefaults(p.level ?? "QEW1");
  // An admin badge shows only its own switch: IEA Site Survey.
  const adminRows = ADMIN_AUTHORITIES.map((a) => {
    const on = auths.includes(a.key);
    return row(a.label, `<span class="pill ${on ? "green" : "grey"}">${on ? "ON" : "OFF"}</span>`, `<button class="chg" type="button" data-act="auth" data-key="${a.key}">${on ? "Turn Off" : "Turn On"}</button>`);
  }).join("");
  const authRows = PERSON_AUTHORITIES.map((a) => {
    const on = auths.includes(a.key);
    const end = admin || !badgeOn ? "" : `<button class="chg" type="button" data-act="auth" data-key="${a.key}">${on ? "Turn Off" : "Turn On"}</button>`;
    const note = on === defaults.includes(a.key) ? "" : ' <small class="muted">set by hand</small>';
    return row(a.label, `<span class="pill ${on ? "green" : "grey"}">${on ? "ON" : "OFF"}</span>${note}`, end);
  }).join("");

  const card = lotoCardOf(
    {
      lotoTrainedOn: p.lotoTrainedOn ?? null,
      lotoExpiresOn: p.lotoExpiresOn ?? null,
      lotoReportedOn: p.lotoReportedOn ?? null,
      lotoNoneReported: p.lotoNoneReported === true,
      lotoVerifiedOn: p.lotoVerifiedOn ?? null,
      lotoVerifiedByName: p.lotoVerifiedByName ?? null,
      lotoSource: p.lotoSource ?? null,
      lotoLastVerifiedOn: p.lotoLastVerifiedOn ?? null,
    },
    state === "qualified",
    today,
  );
  const lotoTone = card.tone === "green" ? "green" : card.tone === "red" ? "red" : card.tone === "amber" ? "amber" : "grey";
  const verifyBtn = !admin && lotoPending(p) ? `<a class="chg" href="#/people/p/${id}/loto">Verify LOTO Date</a>` : "";

  return `<a class="back" href="#/people">People &amp; Badges</a>
    ${ident(admin ? "AD" : p.qewId.slice(-2), p.name || p.qewId, `${site} · ${admin ? "Admin" : level}`)}
    ${flash}
    ${admin ? '<div class="note">The admin badge is not changed from this page, except its Field Pages switch.</div>' : ""}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Badge</b><small>The QEW ID is the printed bag tag. It never changes.</small></div>${STATE_PILL[state]}</div>
      <div class="rows">
        ${row("QEW ID", `<span class="mono">${esc(p.qewId)}</span>`, '<span class="lock">Locked</span>')}
        ${row("Site", esc(site), '<span class="lock">Locked</span>')}
        ${row("Active", esc(activeLine), activeEnd)}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Details</b><small>Level and contact details</small></div>${admin ? "" : `<a class="chg" href="#/people/p/${id}/edit">Change</a>`}</div>
      <div class="rows">
        ${row("Level", esc(level))}
        ${row("Email", txt(p.email))}
        ${row("Phone", txt(p.phone))}
        ${row("Department", txt(p.department))}
        ${row("Role", txt(p.role))}
        ${row("Supervisor", txt(p.supervisor))}
        ${row("Experience", txt(p.yearsExperience))}
        ${row("Employee ID", txt(p.employeeId))}
      </div>
    </div>
    ${
      admin
        ? `<div class="box">
      <div class="boxhd"><div class="nm"><b>Field Pages</b><small>Admin-only field work. Takes effect at the next app open with signal.</small></div></div>
      <div class="rows">${adminRows}</div>
    </div>`
        : `<div class="box">
      <div class="boxhd"><div class="nm"><b>Authorizations</b><small>${esc(level)} default: ${defaults.map((k) => PERSON_AUTHORITIES.find((a) => a.key === k)?.label).join(", ")}. The JSP flow checks these.</small></div></div>
      <div class="rows">${authRows}</div>
      ${badgeOn ? "" : '<div class="empty">Turn the badge on before changing authorizations.</div>'}
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Tag and LOTO</b><small>LOTO dates come from the QEW. Someone else verifies.</small></div>${verifyBtn}</div>
      <div class="rows">
        ${row("Tag claimed", p.claimedOn || badge?.claimedOn ? usDate(String(p.claimedOn || badge?.claimedOn)) : NOT_SET)}
        ${row("Session", txt(p.claimedSessionId ?? badge?.claimedSessionId))}
        ${row(
          "Tag",
          out ? `<span class="pill grey">OUT OF SERVICE</span> <small class="muted">${esc(badge?.outOfServiceReason ?? "")} · ${usDate(String(badge?.outOfServiceOn))}</small>` : badgeOn ? "Working" : "Switched off",
          out || !badge ? "" : `<a class="chg" href="#/people/p/${id}/out">Take Out of Service</a>`,
        )}
        ${row("LOTO", `<span class="pill ${lotoTone}">${esc(card.title.toUpperCase())}</span> <small class="muted">${esc(lotoRowLine(p, card.line))}</small>`)}
        ${row("LOTO date", p.lotoTrainedOn ? usDate(p.lotoTrainedOn) : NOT_SET)}
        ${row("LOTO expires", p.lotoExpiresOn ? usDate(p.lotoExpiresOn) : NOT_SET)}
      </div>
    </div>`
    }`;
}

/** The QEW's card line talks to the QEW ("You reported"). The admin row says where it came from. */
function lotoRowLine(p: Person, qewLine: string): string {
  if (p.lotoVerifiedOn) return `Verified ${usDate(p.lotoVerifiedOn)}${p.lotoVerifiedByName ? ` by ${p.lotoVerifiedByName}` : ""}`;
  if (p.lotoTrainedOn) return lotoSourceLine(p.lotoSource);
  return p.lotoNoneReported ? "Reported no LOTO authorization" : qewLine.replace(/^No LOTO date reported\.$/, "No date reported");
}

// ---- 3 and 4. Confirms ----

export function authoritySheet(p: Person, key: Authority, isOn: boolean, busy: boolean, error = ""): string {
  const label = authorityLabel(key);
  const first = (p.name || p.qewId).split(" ")[0];
  return sheet(
    isOn ? `Authorize ${p.name} for ${label}?` : `Remove ${label} from ${first}?`,
    `<p>${esc(authorityChangeLine(p.name || p.qewId, key, isOn))} Logged with your name and the time.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go${isOn ? "" : " red"}" type="button" data-act="auth-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : isOn ? "Authorize" : "Remove"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

export function activeSheetPerson(p: Person, badgeOn: boolean, busy: boolean, error = ""): string {
  const first = (p.name || p.qewId).split(" ")[0];
  return badgeOn
    ? sheet(
        `Deactivate ${p.name}?`,
        `<p>Tag ${esc(p.qewId)} stops working at every asset. ${esc(first)}'s name stays on every JSP, training record, and LOTO sign-off. The ID is never given to anyone else.</p>
        ${error ? `<div class="note red">${esc(error)}</div>` : ""}
        <button class="go red" type="button" data-act="person-active-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Deactivate"}</button>
        <button class="go2" type="button" data-act="sheet-cancel">Keep Active</button>`,
      )
    : sheet(
        `Turn ${p.name} back on?`,
        `<p>Tag ${esc(p.qewId)} works again. Authorizations come back as they were. Logged with your name and the time.</p>
        ${error ? `<div class="note red">${esc(error)}</div>` : ""}
        <button class="go" type="button" data-act="person-active-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Turn On"}</button>
        <button class="go2" type="button" data-act="sheet-cancel">Keep Off</button>`,
      );
}

// ---- Details edit ----

// No "Pick" placeholder: a blank dropdown, and the fill color shows it needs filling (rule 2026-10-16).
function listOptions(list: readonly string[], current: string): string {
  const all = current && !list.includes(current) ? [current, ...list] : [...list];
  return `<option value=""${current ? "" : " selected"}></option>${all.map((v) => `<option${v === current ? " selected" : ""}>${esc(v)}</option>`).join("")}`;
}

export function personEditForm(p: Person, entry: PersonInput | null, busy: boolean, error = ""): string {
  const v: PersonInput = entry ?? {
    name: p.name ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    department: p.department ?? "",
    role: p.role ?? "",
    supervisor: p.supervisor ?? "",
    yearsExperience: p.yearsExperience === null || p.yearsExperience === undefined ? "" : String(p.yearsExperience),
    employeeId: p.employeeId ?? "",
    level: p.level ?? "",
  };
  const levels: string[] = [...LEVELS];
  if (p.level && !levels.includes(p.level)) levels.unshift(p.level);
  const input = (name: keyof PersonInput, label: string, attrs = "") =>
    `<label class="r"><span class="k">${esc(label)}</span><span class="v"><input class="fin wide" name="${name}" value="${esc(v[name])}" autocomplete="off" ${attrs}></span><span class="e"></span></label>`;
  const select = (name: keyof PersonInput, label: string, list: readonly string[]) =>
    `<label class="r"><span class="k">${esc(label)}</span><span class="v"><select class="fin wide fill" name="${name}">${listOptions(list, v[name])}</select></span><span class="e"></span></label>`;
  return `<a class="back" href="#/people/p/${encodeURIComponent(p.qewId)}">Cancel</a>
    ${ident(p.qewId.slice(-2), "Change Details", `${p.name} · ${p.qewId}`)}
    <form class="box" id="person-form" data-qew="${esc(p.qewId)}" novalidate>
      <div class="rows">
        ${row("QEW ID", `<span class="mono">${esc(p.qewId)}</span>`, '<span class="lock">Locked</span>')}
        ${input("name", "Name", 'maxlength="80" required')}
        <label class="r"><span class="k">Level</span><span class="v"><select class="fin wide" name="level">${levels.map((l) => `<option value="${esc(l)}"${l === v.level ? " selected" : ""}>${esc(levelLabel(l))}</option>`).join("")}</select>
          <small class="fhint">Changing level does not change the ID or the authorization switches.</small></span><span class="e"></span></label>
        ${input("email", "Email", 'type="email" placeholder="name@alltech.com"')}
        ${input("phone", "Phone", 'type="tel" maxlength="30" placeholder="Mobile number"')}
        ${select("department", "Department", DEPARTMENTS)}
        ${select("role", "Role", ROLES)}
        ${input("supervisor", "Supervisor", 'maxlength="80"')}
        ${select("yearsExperience", "Experience", EXPERIENCE)}
        ${input("employeeId", "Employee ID", 'maxlength="40"')}
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="person-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 5. Add a person ----

export function addPersonForm(data: PeopleData, entry: NewPersonInput | null, busy: boolean, error = ""): string {
  const sites = Object.keys(data.siteNames).sort();
  const v: NewPersonInput = entry ?? { siteNumber: sites.find((s) => s === "01") ?? sites[0] ?? "", name: "", email: "", level: "QEW1" };
  const next = v.siteNumber ? nextTagForSite(data.badges, data.people, v.siteNumber) : null;
  return `<a class="back" href="#/people">Cancel</a>
    ${ident("+", "Add Person", "For anyone who does not claim a tag at a class")}
    <form class="box" id="add-person-form" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Site</span><span class="v"><select class="fin wide" name="siteNumber">${sites
          .map((s) => `<option value="${s}"${s === v.siteNumber ? " selected" : ""}>${esc(`${s} ${data.siteNames[s]}`)}</option>`)
          .join("")}</select></span><span class="e"></span></label>
        ${row("Next free ID", next ? `<span class="chip" id="next-tag">${esc(next)}</span>` : '<span class="warn-text" id="next-tag">No unclaimed tags left at this site.</span>', '<span class="lock">Set by the app</span>')}
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(v.name)}" placeholder="Full name" maxlength="80" autocomplete="off" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Email</span><span class="v"><input class="fin wide" type="email" name="email" value="${esc(v.email)}" placeholder="name@alltech.com" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">Level</span><span class="v"><select class="fin wide" name="level">${LEVELS.map((l) => `<option${l === v.level ? " selected" : ""}>${l}</option>`).join("")}</select></span><span class="e"></span></label>
      </div>
    </form>
    <div class="note">Authorizations start at the level default. Training nodes and a verified LOTO date still apply. Most people join by claiming a tag at a class.</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="add-person-form"${busy || !next ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 6 and 7. Tags ----

export function tagsOverview(data: PeopleData): string {
  const sites = Object.keys(data.siteNames);
  const counts = tagCounts(data.badges, sites);
  const total = counts.reduce((n, c) => n + c.claimed + c.unclaimed + c.out, 0);
  const cell = (s: string, bucket: TagBucket, n: number, label: string) =>
    `<a class="cnt-cell" href="#/people/tags/${s}/${bucket}"><b>${n}</b><small>${label}</small></a>`;
  return `<a class="back" href="#/people">People &amp; Badges</a>
    ${ident("TG", `Tags · ${total}`, `99 per site · ${sites.length} ${sites.length === 1 ? "site" : "sites"}`)}
    ${counts
      .map(
        (c) => `<div class="box"><div class="boxhd"><div class="nm"><b>${esc(`${c.siteNumber} ${data.siteNames[c.siteNumber]}`)}</b></div>
        <span class="pill ${data.siteActive[c.siteNumber] ? "green" : "grey"}">${data.siteActive[c.siteNumber] ? "ACTIVE" : "OFF"}</span></div>
        <div class="cnt">${cell(c.siteNumber, "claimed", c.claimed, "CLAIMED")}${cell(c.siteNumber, "unclaimed", c.unclaimed, "UNCLAIMED")}${cell(c.siteNumber, "out", c.out, "OUT")}</div></div>`,
      )
      .join("")}`;
}

/**
 * One site's QEW tags, opened from hub step 03 (COHESION_CHANGE_LOG.md change 5).
 * The page's back link to the site comes from the page frame.
 */
export function siteTags(data: PeopleData, site: string): string {
  const [c] = tagCounts(data.badges, [site]);
  const name = data.siteNames[site] ?? `Site ${site}`;
  const cell = (bucket: TagBucket, n: number, label: string) => `<a class="cnt-cell" href="#/people/tags/${site}/${bucket}"><b>${n}</b><small>${label}</small></a>`;
  const total = c ? c.claimed + c.unclaimed + c.out : 0;
  return `${ident("TG", `QEW Tags · ${name}`, `${site} ${name} · ${total} tags`)}
    <div class="pb-actions"><button class="ac-tbtn dark pb-print" type="button" data-act="qew-print">Print QEW Tags</button></div>
    <div class="box"><div class="cnt">${c ? `${cell("claimed", c.claimed, "CLAIMED")}${cell("unclaimed", c.unclaimed, "UNCLAIMED")}${cell("out", c.out, "OUT")}` : ""}</div></div>
    <div class="pb-actions"><a class="chg" href="#/people/tags">All Sites</a></div>`;
}

const BUCKET_NAME: Record<TagBucket, string> = { claimed: "Claimed", unclaimed: "Unclaimed", out: "Out of Service" };

export function tagList(data: PeopleData, site: string, bucket: TagBucket): string {
  const list = tagsIn(data.badges, site, bucket);
  const rows = list
    .map((b) => {
      const id = b["Badge ID"];
      if (bucket === "claimed") {
        return `<a class="ar" href="#/people/p/${encodeURIComponent(id)}"><span class="chip">${esc(id)}</span><span class="t"><b>${esc(b["Holder Name"])}</b></span><span class="pill green">CLAIMED</span></a>`;
      }
      if (bucket === "out") {
        const why = b.outOfServiceOn ? `${b.outOfServiceReason ?? "Out of service"} · ${usDate(b.outOfServiceOn)}` : b.disabledReason || "Switched off";
        const holder = b["Holder Name"] ? `<b>${esc(b["Holder Name"])}</b>` : "";
        const href = b["Person ID"] ? ` href="#/people/p/${encodeURIComponent(id)}"` : "";
        return `<a class="ar"${href}><span class="chip">${esc(id)}</span><span class="t">${holder}<small>${esc(why)}</small></span><span class="pill grey">OUT</span></a>`;
      }
      return `<div class="ar"><span class="chip">${esc(id)}</span><span class="t muted">Free to claim</span></div>`;
    })
    .join("");
  return `<a class="back" href="#/people/tags/${esc(site)}">QEW Tags</a>
    ${ident(site, `${BUCKET_NAME[bucket]} Tags · ${list.length}`, `${site} ${data.siteNames[site] ?? ""}`)}
    <div class="box">${rows || '<div class="empty">None.</div>'}</div>
    ${bucket === "claimed" ? '<p class="ro">Tap a tag to open the person who holds it.</p>' : ""}`;
}

// ---- 8. Tag out of service ----

export function outOfServiceForm(p: Person, entry: { reason: string; note: string } | null, busy: boolean, error = ""): string {
  const v = entry ?? { reason: "", note: "" };
  return `<a class="back" href="#/people/p/${encodeURIComponent(p.qewId)}">Cancel</a>
    ${ident(p.qewId.slice(-2), "Tag Out of Service", `${p.qewId} · ${p.name}`)}
    <form class="box" id="out-form" data-qew="${esc(p.qewId)}" novalidate>
      <div class="boxhd sub" style="border-top:0"><div class="nm"><b>Reason</b><small>Required</small></div></div>
      <div class="ticks">${OUT_OF_SERVICE_REASONS.map(
        (r) => `<label class="tick"><input type="radio" name="reason" value="${r}"${r === v.reason ? " checked" : ""}><span class="t"><b>${r}</b></span></label>`,
      ).join("")}</div>
      <div class="rows"><label class="r"><span class="k">Note</span><span class="v"><input class="fin wide" name="note" value="${esc(v.note)}" placeholder="What happened" maxlength="200" autocomplete="off"><small class="fhint">Required for Other.</small></span><span class="e"></span></label></div>
    </form>
    <div class="note">Tag ${esc(p.qewId)} stops working. A reason is required. ${esc(p.name.split(" ")[0] || "Their")}'s own record does not change. How a replacement tag is issued is not decided yet.</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim danger" type="submit" form="out-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Take Out of Service"}</button>`;
}

// ---- LOTO verify (Dashboard mockup screen 6) ----

/** back: the page he came from. People & Badges by default; the Training Portal trainee page from there (Stage 4b). */
export function verifyLotoForm(p: Person, data: PeopleData, today: string, entry: string | null, busy: boolean, error = "", back = `people/p/${encodeURIComponent(p.qewId)}`): string {
  return `<a class="back" href="#/${back}">Cancel</a>
    ${ident(p.qewId.slice(-2), "Verify LOTO Date", p.name)}
    <form class="box" id="verify-form" data-qew="${esc(p.qewId)}" data-back="${esc(back)}" novalidate>
      <div class="rows">
        ${row("Person", `${esc(p.name)} · <span class="mono">${esc(p.qewId)}</span>`)}
        ${row("Site", esc(data.siteNames[p.siteNumber] ?? p.siteNumber))}
        ${row("LOTO date reported", p.lotoTrainedOn ? usDate(p.lotoTrainedOn) : NOT_SET)}
        ${row("Reported", esc(lotoSourceLine(p.lotoSource)))}
        <label class="r"><span class="k">Correct date, if different</span><span class="v"><input class="fin" type="date" name="correctedDate" value="${esc(entry ?? "")}" max="${today}">
          <small class="fhint">Leave blank to confirm as reported. A new date resets the 3-year expiry.</small></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim ok-btn" type="submit" form="verify-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Confirm LOTO Date"}</button>`;
}
